import ModuleBase from '../shared/ModuleBase.js';

/**
 * Layout484 — Scroll-triggered word-by-word reveal animation
 * Wraps words in spans, animates opacity based on scroll position
 */
export default class Layout484 extends ModuleBase {
    constructor(el) {
        super(el);
        this._ticking = false;
        this._init();
    }

    _init() {
        const element = this.$el.querySelector('.animated-text-element[data-animation-enabled="1"]');
        if (!element || element.dataset.processed === 'true') return;

        this._element = element;

        // Clean up any existing spans
        const existingSpans = element.querySelectorAll('span.inline-block');
        existingSpans.forEach((span) => {
            const parent = span.parentNode;
            parent.replaceChild(document.createTextNode(span.textContent), span);
            parent.normalize();
        });

        // Wrap words in spans
        Array.from(element.childNodes).forEach((child) => this._wrapWords(child));
        element.dataset.processed = 'true';

        this._spans = element.querySelectorAll('span.inline-block');

        // Bind scroll
        window.addEventListener('scroll', () => this._requestTick(), { passive: true });
        window.addEventListener('resize', () => this._animate());
        this._animate();
    }

    _wrapWords(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent;
            if (!text.trim()) return;

            const words = text.split(/(\s+)/);

            if (words.length <= 1 && text.trim()) {
                const span = document.createElement('span');
                span.className = 'inline-block transition-opacity duration-500 ease-out';
                span.textContent = text;
                span.style.opacity = '0.25';
                node.parentNode.replaceChild(span, node);
                return;
            }

            if (words.length <= 1) return;

            const fragment = document.createDocumentFragment();
            words.forEach((word) => {
                if (word.trim()) {
                    const span = document.createElement('span');
                    span.className = 'inline-block transition-opacity duration-500 ease-out';
                    span.textContent = word;
                    span.style.opacity = '0.25';
                    fragment.appendChild(span);
                } else if (word) {
                    fragment.appendChild(document.createTextNode(word));
                }
            });

            node.parentNode.replaceChild(fragment, node);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            Array.from(node.childNodes).forEach((child) => this._wrapWords(child));
        }
    }

    _requestTick() {
        if (!this._ticking) {
            requestAnimationFrame(() => {
                this._animate();
                this._ticking = false;
            });
            this._ticking = true;
        }
    }

    _animate() {
        if (!this._element || !this._spans) return;

        const rect = this._element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const startPoint = windowHeight * 0.8;
        const endPoint = windowHeight * 0.2;

        let scrollProgress = 0;

        if (rect.top <= startPoint && rect.bottom >= endPoint) {
            const totalDistance = startPoint - endPoint;
            const currentDistance = startPoint - rect.top;
            scrollProgress = Math.max(0, Math.min(1, currentDistance / totalDistance));
        } else if (rect.bottom < endPoint) {
            scrollProgress = 1;
        }

        const totalWords = this._spans.length;
        const wordsToShow = Math.floor(scrollProgress * totalWords);

        this._spans.forEach((span, index) => {
            span.style.opacity = index <= wordsToShow ? '1' : '0.25';
        });
    }
}
